import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  ShimmeredDetailsList,
  Selection,
  SelectionMode,
  IColumn,
  IStackTokens,
  Stack,
  Text,
} from '@fluentui/react';
import {
  Student,
  SubjectStudents,
  SubjectTeacher,
  Professor,
} from '../../models';
import ChangeRequest from '../ChangeRequest/ChangeRequest';
import { getStudents } from 'src/endpoints/getStudents';
import { useNavigate } from 'react-router-dom';
import { Button } from '@fluentui/react-components';

const stackTokens: IStackTokens = {
  childrenGap: 20,
  padding: 20,
};

const columns: IColumn[] = [
  {
    key: 'StudentId',
    name: 'Matrícula',
    fieldName: 'StudentId',
    minWidth: 100,
    maxWidth: 150,
  },
  {
    key: 'Name',
    name: 'Nombre',
    fieldName: 'Name',
    minWidth: 300,
    maxWidth: 500,
  },
  {
    key: 'Opportunity',
    name: 'Oportunidad',
    fieldName: 'Opportunity',
    minWidth: 100,
    maxWidth: 150,
  },
];

type Props = {
  professor: Professor;
  subject: SubjectTeacher;
  academy: string;
  plan: string;
};

const SubjectDetail: React.FC<Props> = ({
  professor,
  subject,
  academy,
  plan,
}) => {
  const [selectedStudents, setSelectedStudents] = useState<Student[]>([]);
  const [subjectStudents, setSubjectStudents] = useState<
    SubjectStudents | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [showChangeRequest, setShowChangeRequest] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const group = subject.Group ? subject.Group.toUpperCase() : subject.Group;
    // Normalizar plan: evitar ".0" y asegurar string sin decimales
    const planNormalized =
      typeof subject.Plan === 'number'
        ? String(Math.trunc(subject.Plan))
        : String(subject.Plan).replace(/\.0+$/, '');
    let isMounted = true;
    const fetchStudents = async () => {
      setIsLoading(true);
      try {
        const response = await getStudents(
          planNormalized,
          subject.SubjectCode,
          group
        );
        if (isMounted) {
          setSubjectStudents(response.data);
        }
      } catch {
        if (isMounted) {
          setError('Error fetching alumnos data.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchStudents();

    return () => {
      isMounted = false;
    };
  }, [subject.Plan, subject.SubjectCode, subject.Group]);

  const handleButtonClick = () => {
    setShowChangeRequest(false);
    if (selectedStudents.length === 0) {
      setSelectionError('Debe seleccionar al menos un alumno.');
      return;
    }

    if (selectedStudents.length > 5) {
      setSelectionError('No se pueden seleccionar más de 5 alumnos.');
      return;
    }

    setShowChangeRequest(true);
  };

  const studentsWithKeys = useMemo(
    () =>
      subjectStudents?.Students.map((student, index) => ({
        ...student,
        key: student.StudentId || index,
      })) || [],
    [subjectStudents]
  );

  const selection = useRef<Selection>(
    new Selection({
      onSelectionChanged: () => {
        const selectedItems = selection.current.getSelection() as Student[];
        setSelectedStudents(selectedItems);
      },
    })
  );

  if (isLoading) {
    return <Text variant="xLarge">Cargando...</Text>;
  }

  if (error) {
    return <Text variant="xLarge">{error}</Text>;
  }

  if (!subjectStudents) {
    return (
      <Text variant="xLarge">No hay alumnos inscritos en esta materia</Text>
    );
  }

  if (showChangeRequest) {
    return (
      <ChangeRequest
        alumnos={selectedStudents}
        materiaAlumno={subjectStudents}
        plan={plan}
        profesor={professor}
        academia={academy}
        onBack={() => setShowChangeRequest(false)}
      />
    );
  }

  return (
    <Stack tokens={stackTokens}>
      {/* Botón de "Volver" en su propia fila en la esquina superior izquierda */}
      <Stack horizontal horizontalAlign="start">
        <Button
          appearance="secondary"
          size="large"
          style={{
            backgroundColor: '#f5f5f5',
            border: '1px solid #ccc',
          }}
          onClick={() => navigate('/')}
        >
          Volver
        </Button>
      </Stack>

      {/* Fila principal de información */}
      <Stack horizontal tokens={stackTokens} horizontalAlign="space-between">
        <Text variant="xLarge">Materia: {subject.SubjectName}</Text>
        <Text variant="xLarge">Clave Materia: {subject.SubjectCode}</Text>
        <Text variant="xLarge">Grupo: {subject.Group}</Text>
        {selectionError && (
          <Text variant="xLarge" styles={{ root: { color: 'red' } }}>
            {selectionError}
          </Text>
        )}
        <Button appearance="primary" onClick={handleButtonClick}>
          Cambiar Calificación
        </Button>
      </Stack>

      {/* Lista de detalles de alumnos */}
      <ShimmeredDetailsList
        items={studentsWithKeys}
        columns={columns}
        layoutMode={1}
        enableShimmer={false}
        selection={selection.current}
        selectionMode={SelectionMode.multiple}
      />
    </Stack>
  );
};

export default SubjectDetail;
